import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getFormValues } from 'redux-form';
import isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';

import {
  HeadingText,
  Tooltip,
  Container,
  Row,
  BodyText,
  Button,
  V2PaypalIcon,
  CaptionText,
  DropdownMenu,
} from '@paypalcorp/pp-react';
import getContent from 'pp-react-l10n';

import SingleAmount from '../buttonManager/buttonOverview/components/singleAmount';
import RecurringCheckbox from '../buttonManager/buttonOverview/components/recurringCheckbox';
import CoverFeeCheckbox from '../../../components/coverFee/coverFeeCheckbox';
import Programs from '../../../components/programs/Programs';
import { LOGO_TARGET_PROFILE } from '../../reducers/reducerButtonFactoryV3';
import getLocaleMessages from '../../utils/localesMessages';
import {
  isGuestFormPreview,
  isHostedFlow,
} from '../../utils/buttonFactoryUtils';
import { AMOUNT_OPTIONS } from '../../utils/constants';
import PresetAmountPreviewComponent from '../buttonManager/buttonOverview/components/PresetAmountPreviewComponent';
import { BTN_FACTORY_V3_FORM } from '../../clientConstants';
import NoteComponent from '../../../components/note';
import DonorPaymentInfoSkeleton from './DonorPaymentInfoSkeleton';
import VoucherPreviewComponent from './VoucherPreviewComponent';
import withTheme from '../withTheme';
import { appendCdnHostName } from '../../../../../lib/geoUtils';
import PPCC from '../ppcc';
import { MERCHANT_PPCC_CONFIRMED_STATUS } from '../../../utility/constants';
import { getPPCCStatus } from '../../../utility/helper';

const {
  phonePreview: {
    cancelAndReturn,
    donateTo,
    donateWithPayPal,
    donateWithCard,
    otherAmount,
  } = {},
} = getLocaleMessages('buttonsV3/buttonOverview');

const { phoneComponent: { voucher } = {} } =
  getLocaleMessages('buttonsV3/partner');

const { initialNote } = getLocaleMessages('buttonsV3/note');
const { previewMessage } = getLocaleMessages('buttonsV3/imageBanner');
const { logoPreviewMessage } = getLocaleMessages('buttonsV3/logoExperience');
const {
  preview: { donateNow },
  mailingAddressLabel,
  mailingAddressPrimaryText,
  mailingAddressHelperText,
} = getLocaleMessages('buttonsV3/preview');
const {
  paypalBranding: { poweredBy },
} = getLocaleMessages('buttonsV3/buttonPreview');

const termsContent = getContent('locals')('buttonsV3/preview')('terms', null, {
  useHTML: true,
});

const CommonPreviewComponent = ({
  formattedButton,
  formValues,
  isMobile = false,
  currentStep,
  isButtonOverviewPreview = false,
  match = {},
  isSDKPreview = false,
}) => {
  const buttonDetails = !isButtonOverviewPreview ? formValues : formattedButton;
  const {
    amountType,
    amountValue = '0',
    cancelReturn,
    coverFeesEnabled,
    receiverName,
    logoTarget,
    logoUrl,
    businessProfileLogoUrl,
    merchantAmount1,
    merchantAmount2,
    merchantAmount3,
    noCancelReturn,
    offerProgram,
    listPrograms,
    paymentSettingsLogoUrl,
    programNames = [],
    purpose,
    receiverCurrency,
    recurringEnabled,
    noteLabel,
    noBannerImage,
    bannerImageUrl,
    noLogoImage,
    version,
  } = buttonDetails;
  const isVoucherFlow = !isEmpty(offerProgram);

  const getLogoUrl = () => {
    const currentUrl = window.location.href;
    // Covering the legacy overview page just in case
    // /donate/buttons/:buttonId
    const isManageRoute =
      currentUrl.indexOf('/manage') >= 0 ||
      (!isEmpty(_get(match, 'params.buttonId')) &&
        currentUrl.indexOf('/edit') < 0 &&
        currentUrl.indexOf('/duplicate') < 0);

    if (logoTarget === LOGO_TARGET_PROFILE) {
      if (isManageRoute) {
        return isEmpty(version)
          ? paymentSettingsLogoUrl || businessProfileLogoUrl
          : businessProfileLogoUrl;
      }
      return businessProfileLogoUrl;
    }
    return logoUrl;
  };

  const iconUrl = getLogoUrl();
  const displayLogo = noLogoImage === 'no';
  const isPresetAmount = amountType === AMOUNT_OPTIONS.AMOUNT_TYPE_PRESET;
  const amountOptions = [
    ...(isPresetAmount
      ? [merchantAmount1, merchantAmount2, merchantAmount3]
      : []),
  ];
  const displayGuestFormPreview =
    isGuestFormPreview(currentStep) && !isButtonOverviewPreview;
  const displayAddressCheckbox =
    displayGuestFormPreview && formValues.noShipping === 'no';
  const displayNote = displayGuestFormPreview && formValues.noNote === 'no';
  const note = noteLabel ? noteLabel : initialNote;
  const showCancelOrReturn =
    noCancelReturn === 'no' && !isEmpty(cancelReturn) && !isSDKPreview;
  const displayBanner = noBannerImage === 'no';
  const isGuestFormMobileorSdk =
    displayGuestFormPreview && (isSDKPreview || isMobile);
  const displayDivider =
    isMobile &&
    !isSDKPreview &&
    (!displayGuestFormPreview ? showCancelOrReturn : true);

  if (!isEmpty(formValues) || !isEmpty(formattedButton)) {
    let amountCurrency = isEmpty(receiverCurrency)
      ? formattedButton.receiverCurrency
      : receiverCurrency;

    const renderAmount = () => {
      if (displayGuestFormPreview) {
        return (
          <SingleAmount
            amount={isPresetAmount ? merchantAmount1 : amountValue}
            currencyCode={amountCurrency}
            isMobile={isMobile}
          />
        );
      }

      return isPresetAmount ? (
        <PresetAmountPreviewComponent
          amountOptions={amountOptions}
          currencyCode={amountCurrency}
          otherAmountLabel={otherAmount}
          isMobile={isMobile}
        />
      ) : (
        <SingleAmount
          amount={amountValue}
          currencyCode={amountCurrency}
          isMobile={isMobile}
        />
      );
    };

    return (
      <>
        <div
          className={`common-preview-container ${
            displayGuestFormPreview ? 'guest-form-preview' : ''
          }`}
        >
          {displayBanner && (
            <div className="img-container">
              {isEmpty(bannerImageUrl) ? (
                <section className={'no-banner-img'}>
                  {<span>{previewMessage}</span>}
                </section>
              ) : (
                <img
                  src={bannerImageUrl}
                  className="banner-image"
                  aria-hidden="true"
                  data-testid="banner-preview"
                />
              )}
            </div>
          )}
          <Container
            className={`common-preview-component ${
              displayBanner ? 'banner-padding' : 'no-banner-padding'
            }`}
          >
            {displayLogo && (
              <Row className="common-preview-component-logo-container">
                {isEmpty(iconUrl) ? (
                  <section
                    className="no-logo-img center-aligned"
                    style={{ backgroundImage: `url(${iconUrl})` }}
                  >
                    <span>{logoPreviewMessage}</span>
                  </section>
                ) : (
                  <img
                    src={iconUrl}
                    className="center-aligned"
                    aria-hidden="true"
                    data-testid="logo-preview"
                  />
                )}
              </Row>
            )}

            <Row>
              <BodyText
                className="common-preview-component-header center-aligned bf-margin-top-24"
                size="sm"
              >
                {isVoucherFlow ? voucher.title : donateTo}
              </BodyText>
            </Row>

            <Row>
              <HeadingText
                className="common-preview-component-receiver center-aligned"
                size="sm"
              >
                {receiverName}
                {getPPCCStatus() === MERCHANT_PPCC_CONFIRMED_STATUS && (
                  <span className="preview-verified-account-icon">
                    <PPCC />
                  </span>
                )}
              </HeadingText>
            </Row>
            {!displayGuestFormPreview && purpose ? (
              <Row>
                <BodyText className="common-preview-component-purpose">
                  {purpose}
                </BodyText>
              </Row>
            ) : null}

            {renderAmount()}

            {!displayGuestFormPreview && listPrograms === 'yes' && (
              <Programs programNames={programNames} isDisabled={true} />
            )}

            {!displayGuestFormPreview && recurringEnabled === 'yes' && (
              <div className="bf-margin-top-24 recurring-checkbox-holder">
                <RecurringCheckbox />{' '}
                <span className="hide-for-button-overview">
                  <Tooltip name="recurring-tooltip-preview" />
                </span>
              </div>
            )}

            {!displayGuestFormPreview &&
              coverFeesEnabled === 'yes' &&
              isHostedFlow() && (
                <div className="bf-margin-top-24">
                  <CoverFeeCheckbox isDisabled={true} />
                </div>
              )}

            {displayNote && <NoteComponent label={note} isNotePreview={true} />}

            {displayGuestFormPreview && <DonorPaymentInfoSkeleton />}

            {displayAddressCheckbox && (
              <>
                <BodyText strong className="mailingAddressText">
                  {mailingAddressLabel}
                </BodyText>
                <DropdownMenu
                  id="mailingAddressPreview"
                  label={mailingAddressPrimaryText}
                  helperText={mailingAddressHelperText}
                  onChange={() => {}}
                  tabIndex="-1"
                />
              </>
            )}

            {isVoucherFlow && (
              <VoucherPreviewComponent
                offerProgram={offerProgram}
                receiverName={receiverName}
              />
            )}

            {displayGuestFormPreview && (
              <CaptionText className="guest-terms-preview">
                {termsContent}
              </CaptionText>
            )}

            <Row>
              {!displayGuestFormPreview ? (
                <Button
                  branded
                  iconComponent={V2PaypalIcon}
                  className={`common-preview-component-btn bf-margin-top-24 donate-with-paypal ${
                    displayGuestFormPreview ? 'margin-bottom-0' : ''
                  }`}
                  disabled
                >
                  {donateWithPayPal}
                </Button>
              ) : (
                <Button
                  className={`common-preview-component-btn bf-margin-top-24 ${
                    displayGuestFormPreview ? 'margin-bottom-0' : ''
                  }`}
                  disabled
                >
                  {donateNow}
                </Button>
              )}
            </Row>

            {!displayGuestFormPreview && (
              <Row>
                <Button
                  secondary
                  className="common-preview-component-btn"
                  disabled
                >
                  {donateWithCard}
                </Button>
              </Row>
            )}
          </Container>
          {displayDivider && <div className="top-border"></div>}
          {showCancelOrReturn && (
            <BodyText
              id="cancel-or-return-preview"
              className={`cancel-or-return-preview-component ${
                isMobile ? 'is-mobile' : ''
              }
                ${
                  !displayGuestFormPreview && isMobile
                    ? 'powered-by-not-showing'
                    : ''
                } `}
              strong
            >
              {cancelAndReturn.replace('{orgName}', receiverName)}
            </BodyText>
          )}
          {isGuestFormMobileorSdk && (
            <div
              className={`powered-by-container ${isMobile ? 'is-mobile' : ''}`}
            >
              <BodyText>{poweredBy}</BodyText>
              <img
                src={appendCdnHostName(
                  '/paypal-ui/logos/svg/paypal-wordmark-color.svg'
                )}
                id="logoImage"
                style={{ height: '24px' }}
              />
            </div>
          )}
        </div>
      </>
    );
  }

  return null;
};

CommonPreviewComponent.propTypes = {
  formattedButton: PropTypes.object,
  formValues: PropTypes.object,
  isMobile: PropTypes.bool,
  showVoucherFlow: PropTypes.bool,
  currentStep: PropTypes.number,
  isButtonOverviewPreview: PropTypes.bool,
};

const mapStateToProps = (state) => {
  const formValues = getFormValues(BTN_FACTORY_V3_FORM)(state) || {};

  return {
    formValues,
    formattedButton:
      state.buttonFactoryV3?.formattedButton ||
      state.buttonManagerV3?.formattedButton,
    currentStep: state.buttonFactoryV3?.currentStep,
  };
};

export default connect(mapStateToProps)(withTheme(CommonPreviewComponent));
